import {
  ManualCostSource,
  getAdditionalCost,
  getEstimateNumberValueBasedOnSettings,
  getEstimateTotal,
  manualCostSources,
  measurementSources,
} from "@Actions/estimation";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import type { GridRowBase, SlickgridReactInstanceX } from "@CubeComponents/html/SlickGrid1";

/** `DataSet` shares no field with the weak `GridRowBase`, so the grid view of it adds the marker fields. */
type DataSetRow = DataSet & GridRowBase;
import { Measurements } from "@Features/viewer-modals";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import type { EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type AddReferencesProps = {
  source: ManualCostSource;
  estimateId: ObjectIdString;
  fileVersionId?: string;
  rowSelected: () => void;
};

type DataSet = {
  value: number;
  name: string;
};

const AddReferences = forwardRef(({ source, estimateId, rowSelected, fileVersionId }: AddReferencesProps, ref) => {
  const { t } = useTranslation();
  const [data, setData] = useState<DataSet[]>();
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;

  const init = useCallback(async () => {
    const [additionalCostRes, estimateTotalRes] = await Promise.all([
      getAdditionalCost({ estimateId }),
      getEstimateTotal({ estimateId }),
    ]);
    if (additionalCostRes.data.status && estimateTotalRes.data.status) {
      setData([
        ...(estimateTotalRes.data.data
          ? [
              {
                name: "Total",
                value: estimateTotalRes.data.data,
              },
            ]
          : []),
        // `getAdditionalCostResponse.result` is `Record<string, unknown>[]` on the wire and
        // `additionalCostItemSchema` declares both `name` and `value` optional, so normalise each
        // row here instead of asserting the grid's row shape onto it. `Number()` is what the
        // amount column's `_valueGetter` already applies to this field.
        ...(additionalCostRes.data.result ?? []).map((row) => ({
          name: String(row.name ?? ""),
          value: Number(row.value),
        })),
      ]);
    }
  }, [estimateId]);

  useEffect(() => {
    if (source == manualCostSources.ADDITIONAL_COST && estimateData._id) {
      init();
    }
  }, [estimateData._id, init, source]);

  /**
   * The forwarded ref is `unknown`-typed because the same ref also goes to `Measurements`
   * (`forwardRef<unknown, …>`) and its only consumer keeps it at a narrower handle type of its own.
   * Bridge it into the grid's typed ref slot rather than asserting one shape onto the other.
   */
  const setGridRef = useCallback(
    (instance: SlickgridReactInstanceX<DataSetRow> | null) => {
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref) {
        ref.current = instance;
      }
    },
    [ref],
  );

  const [columns] = useState(() =>
    typedColumns<DataSetRow>([
      {
        field: "name",
        id: "name",
        name: t("Particular"),
        textAlign: "Left",
      },
      {
        field: "value",
        id: "value",
        name: t("Amount"),
        textAlign: "Left",
        _valueGetter: (params: DataSet) =>
          getEstimateNumberValueBasedOnSettings(Number(params.value), estimateData, "amount"),
      },
    ]),
  );

  return (
    <div className="w-full h-100">
      {source === manualCostSources.MEASUREMENTS ? (
        <Measurements
          ref={ref}
          source={measurementSources.REFERENCE_MEASUREMENT}
          openAsPopup={true}
          estimateId={estimateId}
          handleRowSelection={rowSelected}
          fileVersionId={fileVersionId}
        />
      ) : (
        <SlickGrid1<DataSetRow>
          ref={setGridRef}
          showPageCount={false}
          columnDefinitions={columns}
          rowHeight={40}
          enableSelectAllCheckbox={true}
          dataset={data}
          onSelectedRowIdsChanged={rowSelected}
        />
      )}
    </div>
  );
});

AddReferences.displayName = "AddReferences";

export default AddReferences;
